import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { type User, type Session, type Class, type AuditLog, type SessionModerator, type Recording, type ScheduleApproval } from "@shared/schema";
import { Slider } from "@/components/ui/slider";
import { format } from "date-fns";
import { Shield, Users, FileText, BookOpen, UserCog, UserPlus, X, ChevronDown, ChevronUp, Search, Crown, Trash2, Loader2, Copy, Check, ExternalLink, Filter, BarChart3, Ban, CheckCircle, MoreVertical, Video, Circle, Scissors, Play, ClipboardCheck, RefreshCw, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import AnalyticsTab from "@/components/analytics-tab";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [, navigate] = useLocation();

  const needsRedirect = !isLoading && (!user || (user.role !== "ADMIN" && user.role !== "SUPER_ADMIN"));

  useEffect(() => {
    if (needsRedirect) {
      navigate("/login");
    }
  }, [needsRedirect, navigate]);

  if (isLoading || needsRedirect) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] bg-background" data-testid="status-admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="glass-page-bg" />
      <div className="mb-8">
        <h1 className="text-2xl font-bold sm:text-3xl glow-text" data-testid="text-admin-title">
          <Shield className="mr-2 inline h-6 w-6 text-primary" />
          {t("admin.panel")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("admin.manageDesc")}
        </p>
      </div>

      <Tabs defaultValue="users">
        <div className="mb-6 w-full overflow-x-auto">
          <TabsList className="inline-flex w-auto glass-tabs">
            <TabsTrigger value="users" className="glass-tab-trigger" data-testid="tab-users">
              <Users className="mr-1.5 h-4 w-4" />
              {t("admin.users")}
            </TabsTrigger>
            <TabsTrigger value="classes" className="glass-tab-trigger" data-testid="tab-classes">
              <BookOpen className="mr-1.5 h-4 w-4" />
              {t("admin.classes", { defaultValue: "Classes" })}
            </TabsTrigger>
            <TabsTrigger value="sessions" className="glass-tab-trigger" data-testid="tab-sessions">
              <LiveBroadcastIcon size={16} className="mr-1.5" />
              {t("admin.sessions")}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="glass-tab-trigger" data-testid="tab-analytics">
              <BarChart3 className="mr-1.5 h-4 w-4" />
              {t("admin.analytics", { defaultValue: "Analytics" })}
            </TabsTrigger>
            <TabsTrigger value="recordings" className="glass-tab-trigger" data-testid="tab-recordings">
              <Video className="mr-1.5 h-4 w-4" />
              Recordings
            </TabsTrigger>
            <TabsTrigger value="approvals" className="glass-tab-trigger" data-testid="tab-approvals">
              <ClipboardCheck className="mr-1.5 h-4 w-4" />
              {t("admin.approvals", { defaultValue: "Approvals" })}
            </TabsTrigger>
            <TabsTrigger value="audit" className="glass-tab-trigger" data-testid="tab-audit">
              <FileText className="mr-1.5 h-4 w-4" />
              {t("admin.auditLog")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="classes">
          <ClassesTab />
        </TabsContent>
        <TabsContent value="sessions">
          <SessionsTab />
        </TabsContent>
        <TabsContent value="recordings">
          <RecordingsTab />
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>
        <TabsContent value="approvals">
          <ApprovalsTab />
        </TabsContent>
        <TabsContent value="audit">
          <AuditTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
  });

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    let result = users;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    if (roleFilter !== "ALL") {
      result = result.filter(u => u.role === roleFilter);
    }
    if (statusFilter !== "ALL") {
      result = result.filter(u => (u.status || "ACTIVE") === statusFilter);
    }
    return result;
  }, [users, searchQuery, roleFilter, statusFilter]);

  const changeRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      await apiRequest("PATCH", `/api/admin/users/${userId}/role`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: t("admin.roleUpdated") });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: string }) => {
      await apiRequest("PATCH", `/api/admin/users/${userId}/status`, { status });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: vars.status === "DISABLED" ? t("admin.userDisabled", { defaultValue: "User disabled" }) : t("admin.userEnabled", { defaultValue: "User enabled" }) });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: t("admin.userDeleted", { defaultValue: "User deleted" }) });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const canModifyUser = (targetUser: User) => {
    if (targetUser.id === currentUser?.id) return false;
    if (isSuperAdmin) return targetUser.role !== "SUPER_ADMIN" || false;
    if (targetUser.role === "SUPER_ADMIN" || targetUser.role === "ADMIN") return false;
    return true;
  };

  const canDeleteUser = (targetUser: User) => {
    if (targetUser.id === currentUser?.id) return false;
    if (targetUser.role === "SUPER_ADMIN") return false;
    if (targetUser.role === "ADMIN" && !isSuperAdmin) return false;
    return true;
  };

  const canChangeRole = (targetUser: User) => {
    if (targetUser.id === currentUser?.id) return false;
    if (isSuperAdmin) return true;
    if (targetUser.role === "SUPER_ADMIN" || targetUser.role === "ADMIN") return false;
    return true;
  };

  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);

  const availableRoles = (targetUser: User) => {
    if (isSuperAdmin) {
      return ["USER", "PROVIDER", "MODERATOR", "ADMIN", "SUPER_ADMIN"];
    }
    return ["USER", "PROVIDER", "MODERATOR"];
  };

  if (isLoading) {
    return <div className="glass-panel p-6"><Skeleton className="h-32 w-full" /></div>;
  }

  const roleLabels: Record<string, string> = {
    SUPER_ADMIN: t("admin.superAdmin"),
    ADMIN: t("admin.adminRole"),
    PROVIDER: t("admin.providerRole"),
    MODERATOR: t("admin.moderatorRole"),
    USER: t("admin.userRole"),
  };

  const disabledCount = users?.filter(u => (u.status || "ACTIVE") === "DISABLED").length || 0;

  const StatusBadge = ({ status }: { status: string }) => {
    const isActive = status !== "DISABLED";
    return (
      <Badge variant={isActive ? "outline" : "destructive"} className={`text-[10px] px-1.5 py-0 ${isActive ? "border-emerald-500/40 text-emerald-400" : ""}`}>
        {isActive ? t("admin.active", { defaultValue: "Active" }) : t("admin.disabled", { defaultValue: "Disabled" })}
      </Badge>
    );
  };

  const UserActions = ({ u }: { u: User }) => {
    const isDisabled = (u.status || "ACTIVE") === "DISABLED";
    const showToggle = canModifyUser(u);
    const showDelete = canDeleteUser(u);
    if (!showToggle && !showDelete) {
      return <span className="text-xs text-muted-foreground/60">{t("admin.protected")}</span>;
    }
    return (
      <div className="flex items-center gap-1">
        {showToggle && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={isDisabled ? t("admin.enableUser", { defaultValue: "Enable User" }) : t("admin.disableUser", { defaultValue: "Disable User" })}
            onClick={() => toggleStatus.mutate({ userId: u.id, status: isDisabled ? "ACTIVE" : "DISABLED" })}
            data-testid={`btn-toggle-status-${u.id}`}
          >
            {isDisabled ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <Ban className="h-4 w-4 text-yellow-400" />}
          </Button>
        )}
        {showDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            title={t("admin.deleteUser", { defaultValue: "Delete User" })}
            onClick={() => setDeleteConfirmUser(u)}
            data-testid={`btn-delete-user-${u.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 glass-panel-sm p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admin.searchUsers")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 glass-input"
            data-testid="input-user-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40 glass-input" data-testid="select-role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="glass-select-content">
            <SelectItem value="ALL">{t("admin.allRoles")}</SelectItem>
            <SelectItem value="SUPER_ADMIN">{t("admin.superAdmin")}</SelectItem>
            <SelectItem value="ADMIN">{t("admin.adminRole")}</SelectItem>
            <SelectItem value="PROVIDER">{t("admin.providerRole")}</SelectItem>
            <SelectItem value="MODERATOR">{t("admin.moderatorRole")}</SelectItem>
            <SelectItem value="USER">{t("admin.userRole")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 glass-input" data-testid="select-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="glass-select-content">
            <SelectItem value="ALL">{t("admin.allStatuses", { defaultValue: "All Statuses" })}</SelectItem>
            <SelectItem value="ACTIVE">{t("admin.active", { defaultValue: "Active" })}</SelectItem>
            <SelectItem value="DISABLED">{t("admin.disabled", { defaultValue: "Disabled" })}</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          {filteredUsers.length} {t("admin.ofUsers", { total: users?.length || 0 })}
          {disabledCount > 0 && (
            <span className="ml-2 text-yellow-400">({disabledCount} {t("admin.disabled", { defaultValue: "disabled" })})</span>
          )}
        </div>
      </div>

      <div className="hidden md:block">
        <div className="glass-panel overflow-hidden" style={{ padding: 0 }}>
          <Table>
            <TableHeader className="glass-table-header">
              <TableRow>
                <TableHead>{t("admin.user")}</TableHead>
                <TableHead>{t("admin.emailCol")}</TableHead>
                <TableHead>{t("admin.role")}</TableHead>
                <TableHead>{t("admin.status", { defaultValue: "Status" })}</TableHead>
                <TableHead>{t("admin.joined")}</TableHead>
                <TableHead>{t("admin.changeRole")}</TableHead>
                <TableHead className="w-12">{t("admin.actions", { defaultValue: "Actions" })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {searchQuery || roleFilter !== "ALL" || statusFilter !== "ALL" ? t("admin.noMatch") : t("admin.noUsers")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => (
                  <TableRow key={u.id} className={(u.status || "ACTIVE") === "DISABLED" ? "opacity-60" : ""} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-muted">
                            {u.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={u.role} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={u.status || "ACTIVE"} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(u.createdAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      {canChangeRole(u) ? (
                        <Select
                          value={u.role}
                          onValueChange={(role) => changeRole.mutate({ userId: u.id, role })}
                        >
                          <SelectTrigger className="w-36" data-testid={`select-role-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles(u).map(r => (
                              <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-muted-foreground/60">{t("admin.protected")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <UserActions u={u} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        {filteredUsers.length === 0 ? (
          <div className="glass-panel-sm p-6 text-center text-muted-foreground">
            {searchQuery || roleFilter !== "ALL" || statusFilter !== "ALL" ? t("admin.noMatch") : t("admin.noUsers")}
          </div>
        ) : (
          filteredUsers.map((u) => (
            <div key={u.id} className={`glass-panel-sm p-4 ${(u.status || "ACTIVE") === "DISABLED" ? "opacity-60" : ""}`} data-testid={`user-card-${u.id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="text-xs bg-muted">
                    {u.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm truncate">{u.name}</span>
                    <RoleBadge role={u.role} />
                    <StatusBadge status={u.status || "ACTIVE"} />
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{u.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("admin.joined")}: {format(new Date(u.createdAt), "MMM d, yyyy")}
                  </p>
                  {canChangeRole(u) && (
                    <div className="mt-2">
                      <Select
                        value={u.role}
                        onValueChange={(role) => changeRole.mutate({ userId: u.id, role })}
                      >
                        <SelectTrigger className="w-full" data-testid={`select-role-mobile-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles(u).map(r => (
                            <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    {canModifyUser(u) && (
                      <Button
                        variant={(u.status || "ACTIVE") === "DISABLED" ? "outline" : "secondary"}
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => toggleStatus.mutate({ userId: u.id, status: (u.status || "ACTIVE") === "DISABLED" ? "ACTIVE" : "DISABLED" })}
                        data-testid={`btn-toggle-status-mobile-${u.id}`}
                      >
                        {(u.status || "ACTIVE") === "DISABLED" ? (
                          <><CheckCircle className="mr-1 h-3 w-3" />{t("admin.enable", { defaultValue: "Enable" })}</>
                        ) : (
                          <><Ban className="mr-1 h-3 w-3" />{t("admin.disable", { defaultValue: "Disable" })}</>
                        )}
                      </Button>
                    )}
                    {canDeleteUser(u) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setDeleteConfirmUser(u)}
                        data-testid={`btn-delete-mobile-${u.id}`}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />{t("admin.delete", { defaultValue: "Delete" })}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!deleteConfirmUser} onOpenChange={(open) => !open && setDeleteConfirmUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.confirmDeleteUser", { defaultValue: "Delete User?" })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("admin.deleteUserWarning", {
              defaultValue: "This will permanently delete {{name}} ({{email}}) and all their associated data including messages, reactions, and attendance records. This action cannot be undone.",
              name: deleteConfirmUser?.name,
              email: deleteConfirmUser?.email,
            })}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmUser(null)} data-testid="btn-cancel-delete">
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirmUser) {
                  deleteUser.mutate(deleteConfirmUser.id);
                  setDeleteConfirmUser(null);
                }
              }}
              data-testid="btn-confirm-delete"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("admin.deleteUser", { defaultValue: "Delete User" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type AdminClass = Class & { sessions: Session[]; providerName: string; shareUrl: string | null };

function ClassesTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [providerFilter, setProviderFilter] = useState("ALL");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: classes, isLoading } = useQuery<AdminClass[]>({
    queryKey: ["/api/admin/classes"],
  });

  const { data: users } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
  });

  const deleteClass = useMutation({
    mutationFn: async (classId: string) => {
      await apiRequest("DELETE", `/api/classes/${classId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/classes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sessions"] });
      toast({ title: t("admin.classDeleted", { defaultValue: "Class deleted" }) });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const providers = useMemo(() => {
    if (!users) return [];
    return users.filter(u => u.role === "PROVIDER" || u.role === "ADMIN" || u.role === "SUPER_ADMIN");
  }, [users]);

  const filteredClasses = useMemo(() => {
    if (!classes) return [];
    let result = classes;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.providerName.toLowerCase().includes(q) ||
        c.language.toLowerCase().includes(q)
      );
    }
    if (providerFilter !== "ALL") {
      result = result.filter(c => c.providerId === providerFilter);
    }
    return result;
  }, [classes, searchQuery, providerFilter]);

  const copyShareUrl = async (url: string, classId: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(classId);
    toast({ title: t("admin.linkCopied", { defaultValue: "Share link copied" }) });
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return <div className="glass-panel p-6"><Skeleton className="h-32 w-full" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 glass-panel-sm p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admin.searchClasses", { defaultValue: "Search classes..." })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 glass-input"
            data-testid="input-class-search"
          />
        </div>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-48 glass-input" data-testid="select-provider-filter">
            <Filter className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
            <SelectValue placeholder={t("admin.filterByProvider", { defaultValue: "Filter by provider" })} />
          </SelectTrigger>
          <SelectContent className="glass-select-content">
            <SelectItem value="ALL">{t("admin.allProviders", { defaultValue: "All Providers" })}</SelectItem>
            {providers.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          {filteredClasses.length} {t("admin.ofClasses", { defaultValue: `of ${classes?.length || 0} classes`, total: classes?.length || 0 })}
        </div>
      </div>

      <div className="space-y-3">
        {filteredClasses.length === 0 ? (
          <div className="glass-panel-sm p-8 text-center text-muted-foreground">
            {searchQuery || providerFilter !== "ALL"
              ? t("admin.noClassMatch", { defaultValue: "No classes match your filters" })
              : t("admin.noClasses", { defaultValue: "No classes created yet" })}
          </div>
        ) : (
          filteredClasses.map((cls) => (
            <div key={cls.id} className="glass-panel-sm p-4" data-testid={`admin-class-${cls.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-medium text-sm truncate" data-testid={`text-class-title-${cls.id}`}>
                      {cls.title}
                    </h3>
                    <Badge variant="outline" className="text-[10px]">
                      {cls.language}
                    </Badge>
                    {cls.visibility && (
                      <Badge variant="secondary" className="text-[10px]">
                        {cls.visibility}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("admin.by", { defaultValue: "By" })} {cls.providerName} &middot; {cls.sessions.length} {t("admin.sessionCount", { defaultValue: "sessions" })}
                    {cls.sessions.filter(s => s.status === "LIVE").length > 0 && (
                      <span className="ml-2 text-green-400 inline-flex items-center gap-0.5">
                        <LiveBroadcastIcon size={13} />
                        {cls.sessions.filter(s => s.status === "LIVE").length} live
                      </span>
                    )}
                  </p>
                  {cls.description && (
                    <p className="text-xs text-muted-foreground/70 mt-1 line-clamp-1">
                      {cls.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {cls.shareUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyShareUrl(cls.shareUrl!, cls.id)}
                      className="gap-1.5 text-xs"
                      data-testid={`button-copy-share-${cls.id}`}
                    >
                      {copiedId === cls.id ? (
                        <Check className="h-3.5 w-3.5 text-green-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                      {t("admin.shareLink", { defaultValue: "Share Link" })}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/classes/${cls.id}`, "_blank")}
                    data-testid={`button-view-class-${cls.id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive/70 hover:text-destructive"
                    onClick={() => {
                      if (confirm(t("admin.confirmDeleteClass", { defaultValue: "Are you sure you want to delete this class? All sessions will also be removed." }))) {
                        deleteClass.mutate(cls.id);
                      }
                    }}
                    data-testid={`button-delete-class-${cls.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {cls.shareUrl && (
                <div className="mt-2 flex items-center gap-2 rounded-md bg-muted/20 border border-white/5 px-3 py-1.5">
                  <code className="text-[11px] text-muted-foreground truncate flex-1" data-testid={`text-share-url-${cls.id}`}>
                    {cls.shareUrl}
                  </code>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SessionsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  const { data: sessions, isLoading } = useQuery<(Session & { classTitle: string; providerName: string })[]>({
    queryKey: ["/api/admin/sessions"],
  });

  const forceEnd = useMutation({
    mutationFn: async (sessionId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/end`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sessions"] });
      toast({ title: t("admin.sessionEnded") });
    },
  });

  const deleteSession = useMutation({
    mutationFn: async (sessionId: string) => {
      await apiRequest("DELETE", `/api/sessions/${sessionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/sessions"] });
      toast({ title: t("admin.sessionDeleted") });
    },
  });

  if (isLoading) {
    return <div className="glass-panel p-6"><Skeleton className="h-32 w-full" /></div>;
  }

  return (
    <div className="space-y-3">
      {sessions?.map((s) => (
        <div key={s.id} className="glass-panel-sm p-0" data-testid={`admin-session-${s.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{s.classTitle}</p>
                <p className="text-xs text-muted-foreground">{s.providerName} &middot; {format(new Date(s.startsAt), "MMM d, h:mm a")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={s.status === "LIVE" ? "default" : s.status === "ENDED" ? "secondary" : "outline"}>
                {s.status === "LIVE" && <LiveBroadcastIcon size={13} color="white" className="mr-1" />}
                {s.status}
              </Badge>
              {s.status === "LIVE" && (
                <Button size="sm" variant="destructive" onClick={() => forceEnd.mutate(s.id)} data-testid={`button-force-end-${s.id}`}>
                  {t("admin.forceEnd")}
                </Button>
              )}
              {s.status !== "LIVE" && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    if (confirm(t("admin.confirmDeleteSession"))) deleteSession.mutate(s.id);
                  }}
                  data-testid={`button-delete-session-${s.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setExpandedSession(expandedSession === s.id ? null : s.id)}
                data-testid={`button-expand-session-${s.id}`}
              >
                {expandedSession === s.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {expandedSession === s.id && (
            <div className="border-t border-white/5 px-4 py-3">
              <ModeratorManager sessionId={s.id} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

type ModWithInfo = SessionModerator & { userName: string; userEmail: string };

function ModeratorManager({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);

  const { data: moderators, isLoading } = useQuery<ModWithInfo[]>({
    queryKey: ["/api/sessions", sessionId, "moderators"],
  });

  const { data: allUsers } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
  });

  const addMod = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/moderators`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "moderators"] });
      toast({ title: t("admin.modAssigned") });
      setAddOpen(false);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const removeMod = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/sessions/${sessionId}/moderators/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "moderators"] });
      toast({ title: t("admin.modRemoved") });
    },
  });

  const updatePerms = useMutation({
    mutationFn: async ({ userId, permissions }: { userId: string; permissions: any }) => {
      await apiRequest("PATCH", `/api/sessions/${sessionId}/moderators/${userId}/permissions`, { permissions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "moderators"] });
      toast({ title: t("admin.permUpdated") });
    },
  });

  const togglePerm = (mod: ModWithInfo, key: string) => {
    const perms = { ...(mod.permissions as any) };
    perms[key] = !perms[key];
    updatePerms.mutate({ userId: mod.userId, permissions: perms });
  };

  const modUserIds = new Set(moderators?.map((m) => m.userId) || []);
  const availableUsers = allUsers?.filter((u) => !modUserIds.has(u.id) && u.role !== "ADMIN") || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium flex items-center gap-1.5">
          <UserCog className="h-4 w-4 text-muted-foreground" />
          {t("admin.moderators")}
        </h4>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`button-add-mod-${sessionId}`}>
              <UserPlus className="mr-1 h-3 w-3" />
              {t("admin.assign")}
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-dialog">
            <DialogHeader>
              <DialogTitle>{t("admin.assignModerator")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">{t("admin.noAvailable")}</p>
              ) : (
                availableUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between rounded-md bg-muted/50 p-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-xs bg-muted">
                          {u.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addMod.mutate(u.id)}
                      disabled={addMod.isPending}
                      data-testid={`button-assign-${u.id}`}
                    >
                      {t("admin.assign")}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : moderators && moderators.length > 0 ? (
        <div className="space-y-2">
          {moderators.map((mod) => (
            <div key={mod.id} className="flex items-center justify-between rounded-md bg-muted/50 p-2" data-testid={`mod-row-${mod.userId}`}>
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs bg-muted">
                    {mod.userName?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{mod.userName}</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer toggle-elevate ${(mod.permissions as any)?.canDeleteMessages !== false ? "toggle-elevated" : "opacity-50"}`}
                      onClick={() => togglePerm(mod, "canDeleteMessages")}
                      data-testid={`perm-delete-${mod.userId}`}
                    >{t("admin.deleteMsgs")}</Badge>
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer toggle-elevate ${(mod.permissions as any)?.canMuteUsers !== false ? "toggle-elevated" : "opacity-50"}`}
                      onClick={() => togglePerm(mod, "canMuteUsers")}
                      data-testid={`perm-mute-${mod.userId}`}
                    >{t("admin.mute")}</Badge>
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer toggle-elevate ${(mod.permissions as any)?.canKickUsers !== false ? "toggle-elevated" : "opacity-50"}`}
                      onClick={() => togglePerm(mod, "canKickUsers")}
                      data-testid={`perm-kick-${mod.userId}`}
                    >{t("admin.kick")}</Badge>
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer toggle-elevate ${(mod.permissions as any)?.canEndSession ? "toggle-elevated" : "opacity-50"}`}
                      onClick={() => togglePerm(mod, "canEndSession")}
                      data-testid={`perm-end-${mod.userId}`}
                    >{t("admin.endSession")}</Badge>
                  </div>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeMod.mutate(mod.userId)}
                data-testid={`button-remove-mod-${mod.userId}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-2">{t("admin.noMods")}</p>
      )}
    </div>
  );
}

function AuditTab() {
  const { t } = useTranslation();
  const { data: logs, isLoading } = useQuery<AuditLog[]>({
    queryKey: ["/api/admin/audit-logs"],
  });

  if (isLoading) {
    return <div className="glass-panel p-6"><Skeleton className="h-32 w-full" /></div>;
  }

  return (
    <div className="glass-panel p-0 overflow-hidden">
      <Table>
        <TableHeader className="glass-table-header">
          <TableRow>
            <TableHead>{t("admin.action")}</TableHead>
            <TableHead>{t("admin.target")}</TableHead>
            <TableHead>{t("admin.when")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs && logs.length > 0 ? (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm font-medium">{log.action}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {log.targetType} / {log.targetId}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(log.createdAt), "MMM d, h:mm a")}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                {t("admin.noAuditLogs")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

type AdminRecording = Recording & { classTitle: string; providerName: string; providerId: string; sessionStartsAt: Date };

function fmtDuration(seconds: number | null): string {
  if (!seconds) return "--:--";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function RecordingsTab() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [trimRecording, setTrimRecording] = useState<AdminRecording | null>(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  const { data: recordings, isLoading } = useQuery<AdminRecording[]>({
    queryKey: ["/api/admin/recordings", searchQuery, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set("search", searchQuery);
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const res = await fetch(`/api/admin/recordings?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch recordings");
      return res.json();
    },
  });

  const deleteRecording = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/recordings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/recordings"] });
      setDeleteConfirmId(null);
      toast({ title: "Recording deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const trimMutation = useMutation({
    mutationFn: async ({ id, trimStart, trimEnd }: { id: string; trimStart: number; trimEnd: number }) => {
      const res = await apiRequest("POST", `/api/recordings/${id}/trim`, { trimStart, trimEnd });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/recordings"] });
      setTrimRecording(null);
      toast({ title: "Recording trimmed successfully" });
    },
    onError: (err: any) => toast({ title: "Trim failed", description: err.message, variant: "destructive" }),
  });

  function openTrim(rec: AdminRecording) {
    setTrimRecording(rec);
    setTrimStart(0);
    setTrimEnd(rec.durationSeconds || 0);
  }

  const retryRecording = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/recordings/${id}/retry`);
      return await res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/recordings"] });
      if (data.assembled) {
        toast({ title: "Recording assembled from chunks successfully" });
      } else {
        toast({ title: "Recording set for retry" });
      }
    },
    onError: (err: any) => toast({ title: "Retry failed", description: err.message, variant: "destructive" }),
  });

  const recoverRecording = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/recordings/${id}/recover`);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/recordings"] });
      toast({ title: "Recording recovered from chunks" });
    },
    onError: (err: any) => toast({ title: "Recovery failed", description: err.message, variant: "destructive" }),
  });

  const statusColors: Record<string, string> = {
    READY: "bg-green-500/10 text-green-600 dark:text-green-400",
    RECORDING: "bg-red-500/10 text-red-600 dark:text-red-400",
    PROCESSING: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    PENDING: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    FAILED: "bg-red-500/10 text-red-600 dark:text-red-400",
    PARTIAL: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  };

  const filteredRecordings = recordings || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search recordings, classes, providers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-recordings"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="select-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="READY">Ready</SelectItem>
            <SelectItem value="RECORDING">Recording</SelectItem>
            <SelectItem value="PROCESSING">Processing</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="PARTIAL">Partial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-muted-foreground">
        {filteredRecordings.length} recording{filteredRecordings.length !== 1 ? "s" : ""}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : filteredRecordings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Video className="mx-auto h-10 w-10 mb-2 opacity-50" />
          <p>No recordings found</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title / Class</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecordings.map((rec) => (
              <TableRow key={rec.id} data-testid={`row-recording-${rec.id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm truncate max-w-[250px]" data-testid={`text-recording-title-${rec.id}`}>
                      {rec.title || rec.classTitle}
                    </p>
                    <p className="text-xs text-muted-foreground truncate max-w-[250px]">
                      {rec.classTitle}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm" data-testid={`text-recording-provider-${rec.id}`}>{rec.providerName}</span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className={statusColors[rec.status] || ""} data-testid={`badge-recording-status-${rec.id}`}>
                      {rec.status === "RECORDING" && <Circle className="mr-1 h-2 w-2 fill-red-500 text-red-500 animate-pulse" />}
                      {rec.status}
                    </Badge>
                    {rec.chunkCount > 0 && (
                      <span className="text-[10px] text-muted-foreground" data-testid={`text-chunk-count-${rec.id}`}>
                        {rec.chunkCount} chunk{rec.chunkCount !== 1 ? "s" : ""} saved
                        {rec.durationSeconds && rec.durationSeconds > 0 && (
                          <span className="ml-1">
                            ({Math.min(100, Math.round((rec.chunkCount * 30 / rec.durationSeconds) * 100))}% coverage)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm font-mono" data-testid={`text-recording-duration-${rec.id}`}>
                    {fmtDuration(rec.durationSeconds)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(rec.createdAt), "MMM d, yyyy")}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {(rec.status === "FAILED" || rec.status === "PARTIAL") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-amber-500 hover:text-amber-400"
                        onClick={() => retryRecording.mutate(rec.id)}
                        disabled={retryRecording.isPending}
                        data-testid={`button-retry-recording-${rec.id}`}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        <span className="text-xs">Retry</span>
                      </Button>
                    )}
                    {(rec.status === "FAILED" || rec.status === "RECORDING" || rec.status === "PROCESSING") && rec.chunkCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-orange-500 hover:text-orange-400"
                        onClick={() => recoverRecording.mutate(rec.id)}
                        disabled={recoverRecording.isPending}
                        data-testid={`button-recover-recording-${rec.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        <span className="text-xs">Recover</span>
                      </Button>
                    )}
                    {rec.status === "READY" && rec.url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => window.open(rec.url!, "_blank")}
                        data-testid={`button-play-recording-${rec.id}`}
                      >
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {rec.status === "READY" && rec.durationSeconds && rec.durationSeconds > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openTrim(rec)}
                        data-testid={`button-trim-recording-${rec.id}`}
                      >
                        <Scissors className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => setDeleteConfirmId(rec.id)}
                      data-testid={`button-delete-recording-${rec.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Recording?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete this recording and remove the file from storage. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} data-testid="button-cancel-delete-recording">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteRecording.isPending}
              onClick={() => deleteConfirmId && deleteRecording.mutate(deleteConfirmId)}
              data-testid="button-confirm-delete-recording"
            >
              {deleteRecording.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trimRecording} onOpenChange={(open) => !open && setTrimRecording(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Trim Recording</DialogTitle>
          </DialogHeader>
          {trimRecording && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {trimRecording.title || trimRecording.classTitle} ({fmtDuration(trimRecording.durationSeconds)})
              </p>

              {trimRecording.url && (
                <video
                  src={trimRecording.url}
                  controls
                  className="w-full rounded-lg bg-black"
                  data-testid="video-trim-preview"
                />
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Trim range</span>
                  <span className="font-mono text-muted-foreground">
                    {fmtDuration(trimStart)} — {fmtDuration(trimEnd)}
                  </span>
                </div>
                <div className="px-1">
                  <Slider
                    min={0}
                    max={trimRecording.durationSeconds || 0}
                    step={1}
                    value={[trimStart, trimEnd]}
                    onValueChange={([s, e]) => { setTrimStart(s); setTrimEnd(e); }}
                    data-testid="slider-trim-range"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0:00</span>
                  <span>{fmtDuration(trimRecording.durationSeconds)}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTrimRecording(null)} data-testid="button-cancel-trim">
                  Cancel
                </Button>
                <Button
                  disabled={trimMutation.isPending || trimStart >= trimEnd}
                  onClick={() => trimMutation.mutate({ id: trimRecording.id, trimStart, trimEnd })}
                  data-testid="button-confirm-trim"
                >
                  {trimMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scissors className="mr-2 h-4 w-4" />}
                  Trim Recording
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation();
  const colors: Record<string, string> = {
    SUPER_ADMIN: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    ADMIN: "bg-red-500/10 text-red-600 dark:text-red-400",
    PROVIDER: "bg-primary/10 text-primary",
    MODERATOR: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    USER: "",
  };

  const labels: Record<string, string> = {
    SUPER_ADMIN: t("admin.superAdmin"),
    ADMIN: t("admin.adminRole"),
    PROVIDER: t("admin.providerRole"),
    MODERATOR: t("admin.moderatorRole"),
    USER: t("admin.userRole"),
  };

  return (
    <Badge variant="outline" className={colors[role] || ""}>
      {role === "SUPER_ADMIN" && <Crown className="mr-1 h-3 w-3" />}
      {role === "ADMIN" && <Shield className="mr-1 h-3 w-3" />}
      {role === "PROVIDER" && <BookOpen className="mr-1 h-3 w-3" />}
      {role === "MODERATOR" && <UserCog className="mr-1 h-3 w-3" />}
      {labels[role] || role}
    </Badge>
  );
}

type ApprovalWithMeta = ScheduleApproval & { requesterName: string; reviewerName: string | null; targetLabel: string };

function ApprovalsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { data: approvals, isLoading } = useQuery<ApprovalWithMeta[]>({
    queryKey: ["/api/admin/approvals"],
  });

  const [rejectNote, setRejectNote] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: async ({ approvalId, note }: { approvalId: string; note?: string }) => {
      await apiRequest("POST", `/api/admin/approvals/${approvalId}/approve`, { note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/approvals"] });
      toast({ title: t("admin.approvalApproved", { defaultValue: "Change approved and applied" }) });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ approvalId, note }: { approvalId: string; note?: string }) => {
      await apiRequest("POST", `/api/admin/approvals/${approvalId}/reject`, { note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/approvals"] });
      setRejectingId(null);
      setRejectNote("");
      toast({ title: t("admin.approvalRejected", { defaultValue: "Change request rejected" }) });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="glass-panel p-6"><Skeleton className="h-32 w-full" /></div>;
  }

  const changeTypeLabels: Record<string, string> = {
    CLASS_UPDATE: "Class Update",
    SESSION_SETTINGS_UPDATE: "Session Settings",
    SESSION_RESCHEDULE: "Reschedule",
  };

  if (!approvals || approvals.length === 0) {
    return (
      <div className="glass-panel p-8 text-center">
        <ClipboardCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground" data-testid="text-no-pending-approvals">
          {t("admin.noPendingApprovals", { defaultValue: "No pending approval requests" })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-approvals-list">
      <p className="text-sm text-muted-foreground">
        {approvals.length} {t("admin.pendingRequests", { defaultValue: "pending change request(s) from providers" })}
      </p>
      {approvals.map((a) => (
        <div key={a.id} className="glass-panel p-4" data-testid={`admin-approval-card-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/40">
                  PENDING
                </Badge>
                <Badge variant="secondary" className="text-[10px]">
                  {changeTypeLabels[a.changeType] || a.changeType}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {a.targetType}
                </span>
              </div>
              <p className="text-sm font-medium" data-testid={`admin-approval-target-${a.id}`}>
                {a.targetLabel}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("admin.requestedBy", { defaultValue: "Requested by" })} <span className="font-medium text-foreground">{a.requesterName}</span>
                {" · "}
                {format(new Date(a.createdAt), "MMM d, yyyy 'at' h:mm a")}
              </p>
              {a.proposedChanges && (
                <div className="mt-2 text-xs text-muted-foreground bg-muted/20 rounded p-2">
                  <span className="font-medium">{t("admin.proposedChanges", { defaultValue: "Proposed changes:" })}</span>{" "}
                  {Object.entries(a.proposedChanges as Record<string, any>).map(([key, val]) => (
                    <span key={key} className="inline-block mr-3">
                      <span className="font-medium">{key}:</span> <span className="text-foreground">{String(val)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {rejectingId === a.id ? (
                <div className="space-y-2 w-64">
                  <Textarea
                    placeholder={t("admin.rejectionReason", { defaultValue: "Reason for rejection (optional)" })}
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    className="text-xs h-20"
                    data-testid={`input-reject-note-${a.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => rejectMutation.mutate({ approvalId: a.id, note: rejectNote })}
                      disabled={rejectMutation.isPending}
                      data-testid={`button-confirm-reject-${a.id}`}
                    >
                      {rejectMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <X className="mr-1 h-3 w-3" />}
                      {t("admin.reject", { defaultValue: "Reject" })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectingId(null); setRejectNote(""); }}
                      data-testid={`button-cancel-reject-${a.id}`}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => approveMutation.mutate({ approvalId: a.id })}
                    disabled={approveMutation.isPending}
                    data-testid={`button-approve-${a.id}`}
                  >
                    {approveMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle className="mr-1 h-3 w-3" />}
                    {t("admin.approve", { defaultValue: "Approve" })}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setRejectingId(a.id)}
                    data-testid={`button-reject-${a.id}`}
                  >
                    <X className="mr-1 h-3 w-3" />
                    {t("admin.reject", { defaultValue: "Reject" })}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
